/**
 * Transaction Detail Modal
 *
 * Shows detailed view of a transaction including:
 * - Transaction header (ID, date, type, status)
 * - Bank reconciliation status
 * - Journal entries (debit/credit lines)
 */

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { formatDate, formatCurrency } from '../../utils/formatters';
import { useTransactionDetail } from '../../hooks/react-query/useGeneralLedger';
import {
  TransactionType,
  type GeneralLedger,
  type TransactionDetail,
} from '../../types/drawdown';
import {
  getTypeLabelFull,
  getStatusBadgeStyle,
  getStatusLabel,
} from './transaction-labels';
import {
  Loader2,
  DollarSign,
  TrendingUp,
  Receipt,
  ArrowLeftRight,
  Briefcase,
  BarChart3,
  ShoppingCart,
} from 'lucide-react';

interface TransactionDetailModalProps {
  fundId?: string;
  transactionId: string | null;
  onClose: () => void;
}

// Get icon for transaction type
function getTypeIcon(type: string) {
  switch (type) {
    case TransactionType.DRAWDOWN:
      return <DollarSign className="h-4 w-4" />;
    case TransactionType.INVESTMENT_PURCHASE:
      return <Briefcase className="h-4 w-4" />;
    case TransactionType.FUND_FEE_EXPENSE:
      return <Receipt className="h-4 w-4" />;
    case TransactionType.DISTRIBUTION:
      return <TrendingUp className="h-4 w-4" />;
    case TransactionType.LP_TRANSFER:
      return <ArrowLeftRight className="h-4 w-4" />;
    case TransactionType.VALUATION_CHANGE:
      return <BarChart3 className="h-4 w-4" />;
    case TransactionType.ASSET_SALE:
      return <ShoppingCart className="h-4 w-4" />;
    default:
      return <DollarSign className="h-4 w-4" />;
  }
}

interface TransactionDetailBodyProps {
  isLoading: boolean;
  transaction: TransactionDetail | undefined;
}

function TransactionDetailBody({
  isLoading,
  transaction,
}: TransactionDetailBodyProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!transaction) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        Transaction not found
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Transaction Header */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <Label className="text-xs text-muted-foreground">Reference</Label>
          <div className="font-mono font-medium">
            {transaction.reference || '-'}
          </div>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">
            Transaction Date
          </Label>
          <div>{formatDate(transaction.transaction_date)}</div>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Type</Label>
          <div className="flex items-center gap-2">
            {getTypeIcon(transaction.transaction_type)}
            <span>{getTypeLabelFull(transaction.transaction_type)}</span>
          </div>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Status</Label>
          <div>
            <Badge
              variant="outline"
              className={getStatusBadgeStyle(transaction.status)}
            >
              {getStatusLabel(transaction.status)}
            </Badge>
          </div>
        </div>
      </div>

      {/* Amount and Description */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs text-muted-foreground">Amount</Label>
          <div className="text-xl font-semibold">
            {formatCurrency(transaction.total_amount, {
              currency: transaction.currency,
            })}
          </div>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Posting Date</Label>
          <div>{formatDate(transaction.posting_date)}</div>
        </div>
      </div>

      <div>
        <Label className="text-xs text-muted-foreground">Description</Label>
        <div>{transaction.description}</div>
      </div>

      {/* Journal Entries */}
      <div>
        <Label className="text-xs text-muted-foreground mb-2 block">
          Journal Entries
        </Label>
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[100px]">Account</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-right w-[120px]">Debit</TableHead>
                <TableHead className="text-right w-[120px]">Credit</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {transaction.journal_entries?.map((entry: GeneralLedger) => (
                <TableRow key={entry.id}>
                  <TableCell>
                    <div className="font-mono text-sm">
                      {entry.account_code}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {entry.account_name}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">
                    <div className="text-sm">{entry.description}</div>
                    <div className="text-xs text-muted-foreground">
                      Ref: {entry.reference}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    {entry.debit_amount > 0 && (
                      <span className="text-green-700 font-medium">
                        {formatCurrency(entry.debit_amount, {
                          currency: entry.currency,
                        })}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {entry.credit_amount > 0 && (
                      <span className="text-red-600 font-medium">
                        {formatCurrency(entry.credit_amount, {
                          currency: entry.currency,
                        })}
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {/* Totals Row */}
              {transaction.journal_entries &&
                transaction.journal_entries.length > 0 && (
                  <TableRow className="bg-muted/50 font-medium">
                    <TableCell colSpan={2} className="text-right">
                      Totals
                    </TableCell>
                    <TableCell className="text-right text-green-700">
                      {formatCurrency(
                        transaction.journal_entries.reduce(
                          (sum: number, e: GeneralLedger) =>
                            sum + (e.debit_amount || 0),
                          0
                        ),
                        { currency: transaction.currency }
                      )}
                    </TableCell>
                    <TableCell className="text-right text-red-600">
                      {formatCurrency(
                        transaction.journal_entries.reduce(
                          (sum: number, e: GeneralLedger) =>
                            sum + (e.credit_amount || 0),
                          0
                        ),
                        { currency: transaction.currency }
                      )}
                    </TableCell>
                  </TableRow>
                )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Timestamps */}
      <div className="text-xs text-muted-foreground flex gap-4">
        <span>Created: {formatDate(transaction.created_at)}</span>
        <span>Updated: {formatDate(transaction.updated_at)}</span>
      </div>
    </div>
  );
}

export function TransactionDetailModal({
  fundId,
  transactionId,
  onClose,
}: TransactionDetailModalProps) {
  const { data: transaction, isLoading } = useTransactionDetail(
    fundId,
    transactionId
  );

  if (!transactionId) return null;

  return (
    <Dialog open={!!transactionId} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Transaction Details</DialogTitle>
        </DialogHeader>

        <TransactionDetailBody
          isLoading={isLoading}
          transaction={transaction}
        />
      </DialogContent>
    </Dialog>
  );
}
